import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Link,
  Rating,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey, orange } from "@mui/material/colors";

import { GooglePlaceResult } from "../schema";
import { PlaceImageCarousel } from "./PhotoCarousel";

interface Props {
  isSelected: boolean;
  isAdded: boolean;
  place: GooglePlaceResult;
  onClick?: (place: GooglePlaceResult) => void;
  addPlace: (place: GooglePlaceResult) => void;
}

export function MapListItem({
  isSelected,
  isAdded,
  place,
  onClick,
  addPlace,
}: Props) {
  const hasPhotos = (place.photos?.length ?? 0) > 0;
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const getPriceLevel = () => {
    if (place.priceLevel === google.maps.places.PriceLevel.FREE) {
      return "Free";
    } else if (place.priceLevel === google.maps.places.PriceLevel.INEXPENSIVE) {
      return "$";
    } else if (place.priceLevel === google.maps.places.PriceLevel.MODERATE) {
      return "$$";
    } else if (place.priceLevel === google.maps.places.PriceLevel.EXPENSIVE) {
      return "$$$";
    } else if (
      place.priceLevel === google.maps.places.PriceLevel.VERY_EXPENSIVE
    ) {
      return "$$$$";
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: `1px solid ${grey[300]}`,
        borderColor: isSelected ? "primary.main" : grey[300],
      }}
      onClick={() => onClick && onClick(place)}
    >
      <Grid container>
        <Grid item xs>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: "1 0 auto" }}>
              <Grid container alignItems="center">
                <Grid item xs md={12}>
                  <Typography variant="h6">{place.displayName}</Typography>
                  {place.rating && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: grey[700],
                          fontSize: 12,
                        }}
                      >
                        {place.rating.toFixed(1)}
                      </Typography>
                      <Rating
                        name="read-only"
                        value={place.rating}
                        precision={0.1}
                        color="primary"
                        readOnly
                        icon={
                          <Box
                            component="span"
                            className="fa-solid fa-star"
                            sx={{ fontSize: 12 }}
                          />
                        }
                        emptyIcon={
                          <Box
                            component="span"
                            className="fa-solid fa-star"
                            sx={{ fontSize: 12 }}
                          />
                        }
                        sx={{
                          "& .MuiRating-iconFilled": {
                            color: orange[200],
                          },

                          "& .MuiRating-iconEmpty": {
                            color: grey[300],
                          },
                        }}
                        size="small"
                      />
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: grey[700],
                          fontSize: 12,
                        }}
                        color="textSecondary"
                      >
                        ({place.userRatingCount})
                      </Typography>
                      {place.priceLevel && (
                        <Typography
                          sx={{
                            mt: 0.25,
                            color: grey[700],
                            fontSize: 12,
                          }}
                          color="textSecondary"
                        >
                          · {getPriceLevel()}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography
                    color="textSecondary"
                    paragraph
                    sx={{
                      mt: 0.25,
                      color: grey[700],
                      fontSize: 12,
                      textTransform: "capitalize",
                    }}
                  >
                    {place.primaryTypeDisplayName} · {place.formattedAddress}
                  </Typography>
                </Grid>
                {isMobileScreen && (
                  <Grid item xs="auto" sx={{ pr: 1 }}>
                    <PlaceImageCarousel
                      photos={place.photos}
                      alt={place.displayName}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Grid container gap={1}>
                    <IconButton
                      component={Link}
                      href={place.googleMapsURI}
                      target="blank"
                      size="small"
                      sx={{
                        textTransform: "none",
                        color: grey[600],
                        fontSize: 12,
                      }}
                    >
                      <Box
                        component="span"
                        className="fa-solid fa-map"
                        fontSize="14px"
                      />
                    </IconButton>
                    {place.websiteURI && (
                      <Button
                        component={Link}
                        variant="outlined"
                        href={place.websiteURI}
                        target="blank"
                        size="small"
                        color="inherit"
                        sx={{
                          textTransform: "none",
                          color: grey[600],
                          fontSize: 12,
                        }}
                      >
                        Website{" "}
                        <Box
                          component="span"
                          className="fa-solid fa-up-right-from-square"
                          sx={{ ml: 0.75 }}
                        />
                      </Button>
                    )}
                    <Button
                      disableElevation
                      variant={isAdded ? "outlined" : "contained"}
                      sx={{ textTransform: "none" }}
                      size="small"
                      onClick={() => {
                        addPlace(place);
                      }}
                    >
                      {isAdded ? "Remove" : "Add"}{" "}
                      <Box
                        component="span"
                        className={`fa-solid fa-${isAdded ? "minus" : "plus"}`}
                        sx={{ ml: 0.75 }}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </Grid>
        {hasPhotos && !isMobileScreen && (
          <Grid
            item
            xs="auto"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
              height: "100%",
            }}
          >
            <PlaceImageCarousel photos={place.photos} alt={place.displayName} />
          </Grid>
        )}
      </Grid>
    </Card>
  );
}
